// Match the CSS transitions on .bundle-set__milestones-fill-seg (transform)
// and .bundle-set__milestone-dot::before (clip-path).
const MILESTONE_FILL_MS = 400;
const MILESTONE_DOT_MS = 180;

class CartLaborDaySale extends HTMLElement {
  connectedCallback() {
    this.offerEl = this.querySelector('[data-labor-day-offer]');
    this.milestoneEls = [...this.querySelectorAll('[data-labor-day-milestone]')];
    this.fillSegEls = [...this.querySelectorAll('[data-labor-day-fill-seg]')];
    this.tiers = this._loadTiers();
    const qty = Number.parseInt(this.getAttribute('data-eligible-qty'), 10) || 0;
    this._sync(qty);
  }

  _loadTiers() {
    const el = this.querySelector('[data-labor-day-tiers]');
    if (!el) return [];
    try {
      const raw = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((t) => ({ need: Number(t.need), pct: Number(t.pct) }))
        .filter((t) => Number.isFinite(t.need) && Number.isFinite(t.pct))
        .sort((a, b) => a.need - b.need);
    } catch {
      return [];
    }
  }

  _sync(quantitySum) {
    if (this.offerEl) this.offerEl.innerHTML = this._buildOfferHtml(quantitySum);
    this._syncMilestones(quantitySum);
  }

  _buildOfferHtml(quantitySum) {
    const tiers = this.tiers;
    if (!tiers.length) return '';
    const maxPct = tiers[tiers.length - 1].pct;
    const nextTier = tiers.find((tier) => quantitySum < tier.need) || null;
    if (!nextTier) {
      return `<strong>${maxPct}% OFF UNLOCKED</strong>`;
    }
    const remaining = nextTier.need - quantitySum;
    const itemWord = remaining === 1 ? 'SALE ITEM' : 'SALE ITEMS';
    return `Add <strong>${remaining} MORE ${itemWord}</strong> to unlock <strong>${nextTier.pct}% OFF</strong>`;
  }

  _setReached(state) {
    this.milestoneEls.forEach((el, i) => {
      el.classList.toggle('bundle-set__milestone--reached', Boolean(state[i]));
    });
  }

  _snapMilestones(fills, reached) {
    this.classList.add('is-milestone-restoring');
    this._applyFills(fills, false);
    this._setReached(reached);
    void this.offsetWidth;
    this.classList.remove('is-milestone-restoring');
  }

  _computeFills(quantitySum) {
    const tiers = this.tiers;
    const segs = this.fillSegEls;
    if (!segs.length || segs.length !== tiers.length) return [];
    const lastIdx = tiers.length - 1;
    const fills = [];
    for (let i = 0; i <= lastIdx; i += 1) {
      const prevNeed = i === 0 ? 0 : tiers[i - 1].need;
      const need = tiers[i].need;
      const span = need - prevNeed;
      let fill = 0;
      if (quantitySum > prevNeed) fill = Math.min(1, (quantitySum - prevNeed) / span);
      if (i === lastIdx) {
        const dotScale = Number.parseFloat(segs[i].getAttribute('data-bundle-fill-dot-scale'));
        const scale = Number.isFinite(dotScale) ? dotScale : 1;
        fill = quantitySum > need ? 1 : fill * scale;
      }
      fills.push(fill);
    }
    return fills;
  }

  _applyFills(fills, animate) {
    const segs = this.fillSegEls;
    for (let i = 0; i < fills.length && i < segs.length; i += 1) {
      const seg = segs[i];
      if (!animate) {
        seg.style.transition = 'none';
        seg.style.transform = `scaleX(${fills[i]})`;
        void seg.offsetWidth;
        seg.style.transition = '';
      } else {
        seg.style.transform = `scaleX(${fills[i]})`;
      }
    }
  }

  _syncMilestones(quantitySum) {
    const nextReached = this.milestoneEls.map((el) => {
      const need = Number.parseInt(el.getAttribute('data-labor-day-milestone'), 10);
      return Number.isFinite(need) && quantitySum >= need;
    });
    const fills = this._computeFills(quantitySum);
    const prevReached = window.__jrLdMilestoneReached;
    const prevFills = window.__jrLdMilestoneFills;
    const hasPrev =
      Array.isArray(prevFills)
      && prevFills.length === fills.length
      && Array.isArray(prevReached)
      && prevReached.length === nextReached.length;
    const fillsEqual = hasPrev && prevFills.every((value, i) => value === fills[i]);
    const reachedEqual = hasPrev && prevReached.every((value, i) => Boolean(value) === Boolean(nextReached[i]));

    window.clearTimeout(this._milestoneFillTimer);
    window.clearTimeout(this._milestoneReachTimer);

    if (!fills.length) {
      this._setReached(nextReached);
      window.__jrLdMilestoneReached = nextReached;
      window.__jrLdMilestoneQty = quantitySum;
      return;
    }

    if (fillsEqual && reachedEqual) {
      this._snapMilestones(fills, nextReached);
      window.__jrLdMilestoneReached = nextReached;
      window.__jrLdMilestoneFills = fills;
      window.__jrLdMilestoneQty = quantitySum;
      return;
    }

    // Cart ajax swaps this node. Snap to the last visual frame with transitions
    // off so already-filled dots don't go white and replay. Then animate only
    // the delta. First load (no prev) still plays from empty.
    if (hasPrev) {
      this._snapMilestones(prevFills, prevReached);
    } else {
      this._applyFills(fills.map(() => 0), false);
      this._setReached(nextReached.map(() => false));
    }

    const emptyingDot = hasPrev && prevReached.some((was, i) => was && !nextReached[i]);
    const startFill = () => {
      this._applyFills(fills, true);
      this._milestoneReachTimer = window.setTimeout(
        () => this._setReached(nextReached),
        MILESTONE_FILL_MS,
      );
    };
    if (emptyingDot) {
      this._setReached(prevReached.map((was, i) => was && nextReached[i]));
      this._milestoneFillTimer = window.setTimeout(startFill, MILESTONE_DOT_MS);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(startFill));
    }

    window.__jrLdMilestoneReached = nextReached;
    window.__jrLdMilestoneFills = fills;
    window.__jrLdMilestoneQty = quantitySum;
  }
}

if (!customElements.get('cart-labor-day-sale')) {
  customElements.define('cart-labor-day-sale', CartLaborDaySale);
}

const loadLaborDayEligible = () => {
  const el = document.getElementById('jr-labor-day-eligible-data');
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent || '{}');
    return {
      key: data.key || 'labor-day-eligible',
      variantIds: new Set((data.variantIds || []).map(String)),
    };
  } catch {
    return null;
  }
};

const getAjaxCartState = () => {
  const lac = window.liquidAjaxCart;
  if (lac?.cart && Array.isArray(lac.cart.items)) return lac.cart;
  if (window.__cartJsCache?.get) {
    const cached = window.__cartJsCache.get();
    if (cached && Array.isArray(cached.items)) return cached;
  }
  return null;
};

const findCartLine = (body, cart) => {
  if (!body || !cart?.items?.length) return null;
  if (body.line != null) {
    const idx = Number(body.line) - 1;
    return cart.items[idx] || null;
  }
  if (body.id == null) return null;
  const id = String(body.id);
  return (
    cart.items.find((item) => item.key === id)
    || cart.items.find((item) => String(item.variant_id) === id || String(item.id) === id)
    || null
  );
};

const stampLaborDayPropertiesOnAdd = (body) => {
  const cfg = loadLaborDayEligible();
  if (!cfg?.variantIds?.size || !Array.isArray(body?.items) || !body.items.length) return body;
  return {
    ...body,
    items: body.items.map((item) => {
      const id = String(item?.id ?? item?.variant_id ?? '');
      if (!id || !cfg.variantIds.has(id)) return item;
      return {
        ...item,
        properties: {
          ...(item.properties || {}),
          _d: cfg.key,
        },
      };
    }),
  };
};

// Qty steppers call change.js with only {line, quantity}. If properties are
// omitted or overwritten elsewhere, _d is lost and the discount function
// drops the line. Re-send existing props + ensure _d for eligible variants.
const preserveLaborDayPropertiesOnChange = (body) => {
  if (!body || Number(body.quantity) === 0) return body;

  const cart = getAjaxCartState();
  const lineItem = findCartLine(body, cart);
  if (!lineItem) return body;

  const cfg = loadLaborDayEligible();
  const variantId = String(lineItem.variant_id || lineItem.id || '');
  const existingProps = lineItem.properties && typeof lineItem.properties === 'object'
    ? { ...lineItem.properties }
    : {};
  const isEligible = Boolean(cfg?.variantIds?.has(variantId) || existingProps._d);
  if (!isEligible) return body;

  const properties = {
    ...existingProps,
    ...(body.properties || {}),
  };
  properties._d = existingProps._d || cfg?.key || 'labor-day-eligible';

  return {
    ...body,
    // Prefer line key so Shopify targets the exact property-bearing line.
    id: lineItem.key || body.id,
    line: undefined,
    properties,
  };
};

const patchLiquidAjaxCartLaborDay = () => {
  if (window.__jrLaborDayCartPatched) return true;
  const lac = window.liquidAjaxCart;
  if (!lac || typeof lac.add !== 'function' || typeof lac.change !== 'function') return false;
  window.__jrLaborDayCartPatched = true;

  const originalAdd = lac.add.bind(lac);
  const originalChange = lac.change.bind(lac);

  lac.add = (body, options) => originalAdd(stampLaborDayPropertiesOnAdd(body || {}), options);
  lac.change = (body, options) => originalChange(preserveLaborDayPropertiesOnChange(body || {}), options);
  return true;
};

const ensureLaborDayCartPatch = () => {
  if (patchLiquidAjaxCartLaborDay()) return;
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (patchLiquidAjaxCartLaborDay() || tries > 40) window.clearInterval(timer);
  }, 100);
};

ensureLaborDayCartPatch();
let _swiperRetry = 0;

const initLaborDayUpsellSwiper = () => {
  const root = document.querySelector('[data-labor-day-upsells-swiper]');
  if (!root) return;

  if (typeof window.Swiper === 'undefined') {
    if (_swiperRetry < 20) {
      _swiperRetry += 1;
      window.setTimeout(initLaborDayUpsellSwiper, 100);
    }
    return;
  }
  _swiperRetry = 0;

  const section = root.closest('.cart-labor-day-sale__upsells');
  const prevEl = section?.querySelector('[data-labor-day-upsells-prev]');
  const nextEl = section?.querySelector('[data-labor-day-upsells-next]');
  const slideCount = root.querySelectorAll('.swiper-slide').length;
  if (slideCount < 1) return;

  if (root.swiper) {
    root.swiper.update();
    root.swiper.updateSize();
    root.swiper.updateSlides();
    return;
  }

  new window.Swiper(root, {
    slidesPerView: 3,
    spaceBetween: 8,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
  });
};

const scheduleLaborDayUpsellSwiper = () => {
  window.clearTimeout(scheduleLaborDayUpsellSwiper._t);
  scheduleLaborDayUpsellSwiper._t = window.setTimeout(() => {
    requestAnimationFrame(initLaborDayUpsellSwiper);
  }, 50);
};

const bindLaborDayUpsellObserver = () => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.dataset.laborDaySwiperObs === '1') return;
  drawer.dataset.laborDaySwiperObs = '1';
  const observer = new MutationObserver(() => {
    const el = drawer.querySelector('[data-labor-day-upsells-swiper]');
    if (!el || el.swiper) return;
    scheduleLaborDayUpsellSwiper();
  });
  observer.observe(drawer, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindLaborDayUpsellObserver();
    scheduleLaborDayUpsellSwiper();
  }, { once: true });
} else {
  bindLaborDayUpsellObserver();
  scheduleLaborDayUpsellSwiper();
}

document.addEventListener('liquid-ajax-cart:request-end', scheduleLaborDayUpsellSwiper);
window.addEventListener('cart-open', scheduleLaborDayUpsellSwiper);